import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import sniffer from 'sniffer';
import { Icon, translate } from '@haldor/ui';
import { connect } from 'react-redux';
import { Field, Form, reduxForm, formValueSelector } from 'redux-form';
import { Spinner } from 'UI';
import { Button } from '@haldor/ui';
import { FlexibleTextArea } from 'UI/Inputs';
import Swal from 'sweetalert2';

const ALLOWED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.docx', '.doc', '.xlsx', '.xls', '.pdf', '.pptx', '.ppt', '.txt', '.csv'];
const MAX_FILE_SIZE = 52428800; // 50 MB

class MessageForm extends Component {

	constructor(props) {
		super(props);

		this.input = null;
		this.fileInput = React.createRef();

		this.state = {
			hideInput: false,
			uploading: false,
			selectedFile: null,
		}
	}

	/* Utils */
	_setInput = (element) => {
		this.input = element;
	}

	/* Lifecycle methods */
	componentDidMount = () => {
		if (this.input != null && !sniffer.isDevice) {
			ReactDOM.findDOMNode(this.input).focus();
		}
	}

	/* Events */
	submit = async (values = {}) => {
		const { conversation } = this.props;
		const { selectedFile } = this.state;
		const rawText = typeof values.text === 'string' ? values.text : '';
		const hasText = rawText.trim() !== '';

		if (!hasText && selectedFile == null) {
			return;
		}

		this.setState({ hideInput: true, uploading: true });

		let data = null;
		if (hasText) {
			data = {
				...values,
				conversationID: conversation != null ? conversation.id : null,
				parentId: null,
				text: rawText.replace(/\n/g, "<br />"),
				type: 'text',
			};
		}

		try {
			if (this.props.onSubmit) {
				await this.props.onSubmit(data, selectedFile);
				this.props.reset();
				this.setState({ selectedFile: null });
			} else {
				this.props.reset();
			}
		} finally {
			this.setState({ hideInput: false, uploading: false });
		}
	}

	onFileSelect = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const extension = '.' + file.name.split('.').pop().toLowerCase();
		if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
			Swal.fire({
				title: this.props.translate('Unsupported file type'),
				text: this.props.translate('Supported file types') + ': ' + ALLOWED_FILE_EXTENSIONS.join(', '),
				icon: 'error',
				confirmButtonText: 'OK',
			});
			e.target.value = null;
			return;
		}
		if (file.size > MAX_FILE_SIZE) {
			Swal.fire({
				title: this.props.translate('file-too-big'),
				text: this.props.translate('Size limit for files is') + ' 50 MB',
				icon: 'error',
				confirmButtonText: 'OK',
			});
			e.target.value = null;
			return;
		}

		this.setState({ selectedFile: file });

		e.target.value = null;
	}

	onRemoveFile = (e) => {
		if (e != null) {
			e.preventDefault();
			e.stopPropagation();
		}

		this.setState({ selectedFile: null });
	}

	onAttachClick = (e) => {
		if (e != null) {
			e.preventDefault();
			e.stopPropagation();
		}

		if (this.fileInput.current) {
			this.fileInput.current.click();
		}
	}

	render() {
		const { handleSubmit, submitting, disabled, handleFiles, textValue } = this.props;
		const { selectedFile, uploading } = this.state;
		const hasText = typeof textValue === 'string' && textValue.trim() !== '';
		const isSubmitting = submitting || uploading;
		const canSubmit = hasText || selectedFile != null;
		let placeHolderText = this.props.translate('write-message');
		if (this.props.conversation != null) {
			if (this.props.conversation.status.toUpperCase() == "ARCHIVED") {
				placeHolderText = this.props.translate('conversation-archived');
			}
		}

		return (
			<div className="conversation message-form">
				<Form onSubmit={handleSubmit(this.submit)} className="form form-component">
					<div className="composer-row">
						<div className="input-container">
							<div className="composer-shell">
								{selectedFile != null ?
									<div className="selected-file-badge">
										<Icon name="File" />
										<span className="selected-file-name">{selectedFile.name}</span>
										<button type="button" className="remove-selected-file" onClick={this.onRemoveFile} aria-label={this.props.translate('Remove file')}>
											×
										</button>
									</div>
									: null}

								{!this.state.hideInput ?
									<Field
										component={FlexibleTextArea}
										type="text"
										name="text"
										placeholder={placeHolderText}
										rows={1}
										disabled={disabled}
										ref={this._setInput}
										withRef
										forwardRef
									/>
									: null}
							</div>
						</div>

						<Button disabled={isSubmitting || disabled || !canSubmit}>
							{!isSubmitting ?
								<Icon name="Send" />
								: null}

							{isSubmitting ?
								<Spinner center small />
								: null}
						</Button>
					</div>

					{handleFiles ?
						<div className="attach-file-row">
							<input
								type="file"
								ref={this.fileInput}
								style={{ display: 'none' }}
								accept={ALLOWED_FILE_EXTENSIONS.join(', ')}
								onChange={this.onFileSelect}
							/>
							<Button id="attach-file-button"
								type="secondary"
								onClick={this.onAttachClick}
								disabled={disabled || uploading}
							>
								<Icon name="File" />
								<span className="attach-file-label">{this.props.translate('Attach file')}</span>
							</Button>
						</div> : null}
				</Form>
			</div>
		);
	}
}

function mapStateToProps(state) {
	const selector = formValueSelector('MessageForm');

	return {
		translate: translate(state.Languages.translations),
		textValue: selector(state, 'text'),
	};
}

export default connect(mapStateToProps)(reduxForm({
	form: 'MessageForm',
	destroyOnUnmount: true,
})(MessageForm));
